import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info } from 'lucide-react';
import type { BrandOnboardingStatus } from '@mg/shared';
import { Button } from '../../../components/ui/Button';
import { useDialogs } from '../../../components/ui/DialogProvider';
import { UnsavedChangesDialog } from '../../../components/ui/UnsavedChangesDialog';
import { useFooterSaveState } from '../../../components/settings/footerSave';
import { EDIT_UNSAVED_MESSAGE, ONBOARDING_NOTICE, REVISIT_CONFIRM } from '../../brand/brandCopy';
import { NoticeBox } from '../../brand/NoticeBox';
import { setupPath } from '../../brand/paths';

/**
 * The onboarding notice in the Brand information card (spec:60-68, :188), admins only (E30): the
 * button on the left (Start, Continue or Revisit Brand Setup, by the onboarding status), then the
 * icon and the status text. All three open Brand Setup, the wizard deciding where it starts and
 * whether it revisits (web.md §4.1). In order (spec:68, :555):
 * 1. With unsaved changes on the page, the Unsaved changes dialog first: Keep editing stays; Discard
 *    changes puts the saved values back; Save and continue saves, and stays if the save fails.
 * 2. A finished setup asks "Revisit Brand Setup?" first.
 * 3. Then the page goes, once it has no changes left: navigating in the same tick would meet the leave
 *    guard while it still holds the old "unsaved" answer.
 */
export function OnboardingNotice({ status, slug }: { status: BrandOnboardingStatus; slug: string }) {
  const navigate = useNavigate();
  const { confirm } = useDialogs();
  const footer = useFooterSaveState();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [asking, setAsking] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const copy = ONBOARDING_NOTICE[status];
  const dirty = footer?.dirty ?? false;

  useEffect(() => {
    if (pending === null || dirty) return;
    setPending(null);
    navigate(pending);
  }, [pending, dirty, navigate]);

  const open = async () => {
    if (status === 'FINISHED') {
      const ok = await confirm({
        title: REVISIT_CONFIRM.title,
        message: REVISIT_CONFIRM.message,
        confirmLabel: REVISIT_CONFIRM.confirmLabel,
        cancelLabel: REVISIT_CONFIRM.cancelLabel,
      });
      if (!ok) return;
    }
    setPending(setupPath(slug));
  };

  const onClick = () => {
    if (dirty) setAsking(true);
    else void open();
  };

  return (
    <>
      <NoticeBox
        icon={<Info className="h-4 w-4" aria-hidden />}
        action={
          <Button ref={buttonRef} type="button" onClick={onClick}>
            {copy.button}
          </Button>
        }
      >
        {copy.text}
      </NoticeBox>
      <UnsavedChangesDialog
        open={asking}
        message={EDIT_UNSAVED_MESSAGE}
        saving={footer?.saving ?? false}
        canSave={footer?.canSave ?? false}
        returnFocusTo={buttonRef.current}
        onKeepEditing={() => setAsking(false)}
        onDiscard={() => {
          footer?.discardAll();
          setAsking(false);
          void open();
        }}
        onSave={async () => {
          const ok = (await footer?.saveAll()) ?? false;
          setAsking(false);
          // A failed save stays on the page, where the card shows why.
          if (ok) void open();
        }}
      />
    </>
  );
}
